import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { environment } from '../../../../environments/environment';
import {
  createFileUploadV2Store,
  FileError,
  FileUploadStateFile,
  FileUploadTriggerDirective,
  FileUploadV2Store,
} from '../../shared/directives/file-upload-v2';

/**
 * The skill builder's file-upload control value: persisted-shape entries only
 * (`id === the store/document uuid`). The DTO builders read `id`/`name`, the
 * group validators read `name`/length, and the update diff matches
 * `file.id === doc.uuid` — so `uuid` must NEVER appear as a field name here.
 * Re-homed from the deleted V1 `AssistantSaveModalFileUploadState` (minus
 * `isLoading` — loading is store status now).
 */
export interface AssistantBuilderFileValue {
  id: string;
  name: string;
  extension: string;
  size: number;
}

/** A file-upload status that is staged for persistence (in the control value). */
const PERSISTABLE_STATUSES: ReadonlySet<FileUploadStateFile['status']> =
  new Set(['upload-pending', 'upload-ongoing', 'upload-done'] as const);

const isPersistable = (file: FileUploadStateFile): boolean =>
  PERSISTABLE_STATUSES.has(file.status);

/** Stable key for an id set — cheap change detection for onChange gating. */
const idKey = (ids: string[]): string =>
  [...ids].sort((a, b) => a.localeCompare(b)).join('|');

/** A file chip rendered from store state (loading / error styling per status). */
interface ZoneChip {
  id: string;
  name: string;
  fileType: fromSeriousSystem.FileTypesIconsType;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Skill-builder file-upload zone (SB-3325 PR-5): the successor to the V1
 * save-modal CVA component, rebuilt on the shared file-upload-v2 machinery.
 * Same selector-level UX contract (label, picker button, chips with per-file
 * loading, close-to-remove, disabled/read-only) but backed by a
 * `FileUploadV2Store` instead of the global NgRx `assistantSaveModalFiles`
 * feature.
 *
 * Store resolution (Store-lifecycle decision #2): the zone accepts an optional
 * `[uploadStore]` (the compliance shared-store mechanism) and otherwise lazily
 * creates its own via `createFileUploadV2Store(injector)` — there is NO DI
 * provider, so a bare `inject(FileUploadV2Store)` throws in the builder tree by
 * construction.
 */
@Component({
  selector: 'squadbox-assistant-builder-file-upload',
  imports: [
    NgClass,
    TranslateModule,
    FileUploadTriggerDirective,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.FileChipComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AssistantBuilderFileUploadComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: AssistantBuilderFileUploadComponent,
      multi: true,
    },
  ],
  template: `
    <div class="flex flex-col gap-2 group">
      <!-- Visual caption, not a <label>: the real control is the hidden input
           generated by the trigger directive (no stable id to associate), and
           the picker's visible text is its own accessible name. -->
      <span
        class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5"
        [class]="{
          'text-neutral-400': isDisabled(),
          'text-neutral-700': !isDisabled(),
        }"
      >
        {{ label() }}
      </span>
      @if (resolvedStore(); as store) {
        <button
          type="button"
          squadboxFileUploadTrigger
          [store]="store"
          [accept]="acceptAttribute()"
          [multiple]="multiple()"
          class="
            group
            border hover:border-primary-300 focus:outline-hidden
            rounded-lg pl-4 pr-3 py-2 h-12
            grid grid-cols-[1fr_auto] items-center
            disabled:border-neutral-200 disabled:hover:border-neutral-200 disabled:pointer-events-none
          "
          [class.disabled]="isDisabled()"
          [ngClass]="{
            'border-error-500 hover:border-error-600': currentErrors().length,
            'border-neutral-200 hover:border-primary-300':
              !currentErrors().length,
          }"
          [disabled]="isDisabled()"
        >
          <span
            class="typo-p3 text-neutral-500 group-disabled:text-neutral-400 justify-self-start"
            >{{ placeholder() }}</span
          >
          @if (filePickerButtonIcon() === 'plus') {
            <svg
              sdUseUIBasicIcon="plus"
              class="size-6 shrink-0"
              [class]="{
                'text-neutral-400': isDisabled(),
                'text-neutral-700': !isDisabled(),
              }"
            ></svg>
          } @else {
            <svg
              sdUseArrowIcon="chevron-small-down"
              class="size-6 shrink-0"
              [class]="{
                'text-neutral-400': isDisabled(),
                'text-neutral-700': !isDisabled(),
              }"
            ></svg>
          }
        </button>
      }

      <!-- Chip/error area: only rendered when there are chips or errors, so an
           empty picker is just the 48px button (no reserved blank line below).
           Keeps the field-to-next-element spacing tight per Figma; the reserved
           space (nbsp + negative margins) still positions chips when present. -->
      @if (chips().length || currentErrors().length) {
        <div class="grow">
          <div>
            <div
              class="flex flex-col -mt-1"
              [ngClass]="{
                'justify-between': currentErrors().length,
                'justify-end': !currentErrors().length,
              }"
            >
              @for (message of currentErrors(); track message) {
                <span class="typo-caption text-error-600">{{ message }}</span>
              }
              <!-- Prevents file chips from overlapping the picker button -->
              <p>&nbsp;</p>
            </div>
            <div
              class="flex flex-wrap gap-2 mr-14"
              [class.-mt-5]="!currentErrors().length"
            >
              @for (chip of chips(); track chip.id) {
                <sd-file-chip
                  [filename]="chip.name"
                  [fileType]="chip.fileType"
                  [isLoading]="chip.isLoading"
                  [isError]="chip.isError"
                  [isDisabled]="isDisabled()"
                  rightIcon="close"
                  (rightIconClicked)="onRemove(chip.id)"
                ></sd-file-chip>
              }
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class AssistantBuilderFileUploadComponent
  implements ControlValueAccessor, Validator, OnInit
{
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);

  // Inputs (selector-level UX contract mirrors the V1 component)
  public readonly label = input<string>(
    this.translate.instant('ASSISTANTS.SAVE_MODAL.FORM.FILES.LABEL') as string,
  );
  public readonly placeholder = input<string>(
    this.translate.instant(
      'ASSISTANTS.SAVE_MODAL.FORM.FILES.PLACEHOLDER',
    ) as string,
  );
  public readonly filePickerButtonIcon = input<'chevron' | 'plus'>('chevron');
  public readonly fileSupportedExtensions = input.required<string[]>();
  public readonly transactionId = input.required<string>();
  public readonly multiple = input<boolean>(true);
  /**
   * The compliance shared-store mechanism (Stage C): when the page binds a
   * store, all zones bound to the same instance render identical chips and gate
   * Save together. Unbound zones lazily create their own instance.
   */
  public readonly uploadStore = input<FileUploadV2Store>();

  /** The resolved store — set in ngOnInit; a signal so the template reacts. */
  private readonly store = signal<FileUploadV2Store | null>(null);
  protected readonly resolvedStore = computed(() => this.store());

  public readonly isDisabled = signal(false);

  /** `accept` attribute for the hidden input, from the zone's extension list. */
  protected readonly acceptAttribute = computed(() =>
    this.fileSupportedExtensions()
      .map((type) => `.${type}`)
      .join(','),
  );

  /** Chips rendered from store state (loading/error styling by status). */
  protected readonly chips = computed<ZoneChip[]>(() => {
    const store = this.store();
    if (!store) return [];
    return Object.values(store.state().files).map((file) => ({
      id: file.uuid,
      name: file.name,
      // Edit-mode seeds can arrive with no extension (a document view that only
      // carried id/name); default rather than crash, mirroring the V1 chip.
      fileType: file.extension
        ? fromSeriousSystem.getFileTypeIconFromExtension(file.extension)
        : 'default',
      isLoading:
        file.status === 'upload-pending' || file.status === 'upload-ongoing',
      isError:
        file.status === 'upload-invalid' || file.status === 'upload-failed',
    }));
  });

  /** Distinct translated error messages for the current error chips. */
  protected readonly currentErrors = computed<string[]>(() => {
    const store = this.store();
    if (!store) return [];
    const messages = new Set<string>();
    for (const file of Object.values(store.state().files)) {
      if (file.error !== null) {
        messages.add(this.errorMessage(file.error));
      }
    }
    return [...messages];
  });

  private onChange: (value: AssistantBuilderFileValue[]) => void = () => {
    void 0;
  };
  private onTouched: () => void = () => {
    void 0;
  };
  private onValidatorChange: () => void = () => {
    void 0;
  };

  /** The persistable id set last emitted / reconciled (onChange gate). */
  private lastEmittedIds = '';
  /** Buffered writeValue seed when it fires before ngOnInit resolves the store. */
  private pendingSeed: AssistantBuilderFileValue[] | null = null;

  constructor() {
    // Store state → validators + onChange. Runs on every state mutation once the
    // store resolves. onChange fires only when the PERSISTABLE id set changes
    // (e.g. pick, remove, ongoing→failed drops an id from the value); pure
    // status transitions that keep the set (pending→ongoing→done) don't emit.
    effect(() => {
      const store = this.store();
      if (!store) return;
      store.state(); // track
      this.onValidatorChange();
      this.emitIfChanged(store);
    });
  }

  ngOnInit(): void {
    const store = this.uploadStore() ?? createFileUploadV2Store(this.injector);
    // Idempotent, overwrite semantics (S14): the three compliance slots each
    // configure the same page-owned store on mount.
    store.configure({
      supportedExtensions: this.fileSupportedExtensions(),
      transactionId: this.transactionId(),
      uploadFrom: 'skill_builder',
    });
    if (this.pendingSeed !== null) {
      this.reconcileInto(store, this.pendingSeed);
      this.pendingSeed = null;
    }
    // Set last so the reactive effect fires with the store fully seeded.
    this.store.set(store);
  }

  public onRemove(id: string): void {
    if (this.isDisabled()) return;
    this.store()?.removeFile(id);
  }

  /**
   * The resolved store instance — exposed for the compliance form's dev-mode
   * shared-store assertion (closed-loop invariant #2). Null before ngOnInit.
   */
  public getResolvedStore(): FileUploadV2Store | null {
    return this.store();
  }

  // --- ControlValueAccessor ---

  /**
   * Reconcile store to the incoming persistable value. Buffers when it fires
   * before ngOnInit (never creating a premature own store — which would split
   * the compliance shared-store topology).
   */
  writeValue(value: AssistantBuilderFileValue[] | null): void {
    const incoming = value ?? [];
    const store = this.store();
    if (!store) {
      this.pendingSeed = incoming;
      return;
    }
    this.reconcileInto(store, incoming);
  }

  registerOnChange(fn: (value: AssistantBuilderFileValue[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  // --- Validator ---

  validate(): ValidationErrors | null {
    const store = this.store();
    if (!store) return null;
    const files = Object.values(store.state().files);
    const errors: ValidationErrors = {};
    if (
      files.some(
        (f) => f.status === 'upload-pending' || f.status === 'upload-ongoing',
      )
    ) {
      errors['uploadPending'] = true;
    }
    if (files.some((f) => f.status === 'upload-failed')) {
      errors['uploadFailed'] = true;
    }
    if (files.some((f) => f.status === 'upload-invalid')) {
      errors['uploadInvalid'] = true;
    }
    return Object.keys(errors).length ? errors : null;
  }
  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  // --- internals ---

  /**
   * Persistable-only reconcile (Store-lifecycle decision #5). (i) remove only
   * **upload-done** entries the incoming value dropped — never in-flight
   * pending/ongoing entries (the store owns those; the form value transiently
   * lags a just-picked file, e.g. the compliance mirror patching a sibling
   * before the pick's onChange propagates — dropping them would abort a live
   * upload), and never error chips; (ii) insert missing ids via
   * seedPersistedFiles (never re-upload); (iii) an id already in the store is
   * left entirely untouched; (iv) never calls onChange and is a no-op when the
   * persistable id set already matches. The reactive effect re-emits onChange
   * to pull the form up to the store when they diverge.
   */
  private reconcileInto(
    store: FileUploadV2Store,
    incoming: AssistantBuilderFileValue[],
  ): void {
    const incomingIds = new Set(incoming.map((v) => v.id));
    const stateFiles = Object.values(store.state().files);
    const persistableIds = stateFiles.filter(isPersistable).map((f) => f.uuid);

    const targetKey = idKey([...incomingIds]);
    if (idKey(persistableIds) === targetKey) {
      // No-op: prevents mirror feedback loops (the compliance sync patches
      // siblings on every new array ref).
      this.lastEmittedIds = targetKey;
      return;
    }

    const toRemove = stateFiles
      .filter((f) => f.status === 'upload-done' && !incomingIds.has(f.uuid))
      .map((f) => f.uuid);
    if (toRemove.length) {
      store.removeFiles(toRemove);
    }

    const present = new Set(Object.keys(store.state().files));
    const toSeed = incoming
      .filter((v) => !present.has(v.id))
      .map((v) => ({
        uuid: v.id,
        name: v.name,
        extension: v.extension,
        size: v.size,
      }));
    if (toSeed.length) {
      store.seedPersistedFiles(toSeed);
    }

    // writeValue never calls onChange — just record the new persistable set so
    // the reactive effect doesn't echo it back.
    this.lastEmittedIds = targetKey;
  }

  private emitIfChanged(store: FileUploadV2Store): void {
    const persistable = Object.values(store.state().files).filter(
      isPersistable,
    );
    const key = idKey(persistable.map((f) => f.uuid));
    if (key === this.lastEmittedIds) return;
    this.lastEmittedIds = key;
    this.onChange(
      persistable.map((f) => ({
        id: f.uuid,
        name: f.name,
        extension: f.extension,
        size: f.size,
      })),
    );
    this.onTouched();
  }

  /**
   * Total error map (`Record<Exclude<FileError, null>, string>`): adding a new
   * FileError literal breaks the build here until it is mapped. The store's
   * English literals stay untouched (chat string-matching is load-bearing).
   */
  private errorMessage(error: Exclude<FileError, null>): string {
    const messages: Record<Exclude<FileError, null>, string> = {
      'Invalid file type': this.translate.instant(
        'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_TYPE',
        { types: this.fileSupportedExtensions().join(', ').toUpperCase() },
      ) as string,
      'File size exceeded': this.translate.instant(
        'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_SIZE',
        { size: environment.maxFileUploadSizeInMB },
      ) as string,
      'Empty file': this.translate.instant(
        'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_EMPTY',
      ) as string,
      'Error uploading file': this.translate.instant(
        'ASSISTANTS.SAVE_MODAL.FORM.FILES.ERROR_FILE_UPLOAD',
      ) as string,
    };
    return messages[error];
  }
}
